import React, { useState, useEffect, useMemo } from 'react';
import { Card, Spin, Row, Col, Table, Tag, Tooltip, Empty, Space, DatePicker, Modal } from 'antd';
import { ShopOutlined, CalendarOutlined, WarningFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { yuan } from '../utils/format.js';
import { apiFetch, onDataChanged } from '../utils/api.js';

// 判定 → 语义色（国内红涨绿跌惯例：可持续=好→红，亏损=差→绿）
const VERDICT_META = {
  '可持续': { color: 'red', bg: '#fff1f0', desc: '本月扣费后盈利且投产比 ≥ 30，可加大预算' },
  '可优化': { color: 'orange', bg: '#fff7e6', desc: '本月扣费后盈利但投产比 < 30，优化转化' },
  '亏损': { color: 'green', bg: '#f6ffed', desc: '本月扣费后亏损（利润 < 广告费），建议停投' },
  '未投': { color: 'default', bg: '#fafafa', desc: '未投放广告（自然流量）' },
};

export default function ShopAttribution() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(dayjs()); // 当前月份对象；null = 全部
  const [lossModal, setLossModal] = useState(null); // 亏损明细弹框 { shop, rows, totalAd, totalProfit, totalLoss, count }
  const [lossLoading, setLossLoading] = useState(false);

  // 打开某店铺的亏损明细弹框（点击「日净利连续亏损」提示触发）
  const openLossDetail = (shop) => {
    setLossModal({ shop });
    setLossLoading(true);
    apiFetch(`/api/shop-loss-detail?shop=${encodeURIComponent(shop)}`)
      .then(r => r.json())
      .then(d => { setLossModal({ shop, rows: d.rows || [], totalAd: d.totalAd || 0, totalProfit: d.totalProfit || 0, totalLoss: d.totalLoss || 0, count: d.count || 0 }); setLossLoading(false); })
      .catch(() => setLossLoading(false));
  };

  const load = (m) => {
    setLoading(true);
    const q = m ? `?month=${m.format('YYYY-MM')}` : '?month=all';
    apiFetch('/api/shop-attribution' + q)
      .then(r => r.json())
      .then(d => { setData(d.data || []); setLoading(false); })
      .catch(e => { setLoading(false); });
  };

  useEffect(() => {
    load(month);
    return onDataChanged(() => { load(month); });
  }, [month]);

  // 汇总指标
  const summary = useMemo(() => {
    const totalFee = data.reduce((s, x) => s + (Number(x.fee) || 0), 0);
    const totalRev = data.reduce((s, x) => s + (Number(x.revenue) || 0), 0);
    const totalNet = data.reduce((s, x) => s + (Number(x.netProfit) || 0), 0);
    const totalOrders = data.reduce((s, x) => s + (Number(x.orders) || 0), 0);
    const avgRatio = totalFee > 0 ? totalRev / totalFee : 0;
    const dist = { '可持续': 0, '可优化': 0, '亏损': 0, '未投': 0 };
    for (const x of data) { if (dist[x.verdict] != null) dist[x.verdict] += 1; }
    return { totalFee, totalRev, totalNet, totalOrders, avgRatio, dist };
  }, [data]);

  // 月累计亏损店铺（主口径：判定=亏损，即本月利润 − 广告费 < 0）
  const monthLossShops = useMemo(() => data.filter(x => x.verdict === '亏损'), [data]);
  // 日净利连续亏损、但月累计尚未亏损的店铺（参考口径，提示关注趋势）
  const dailyLossOnly = useMemo(() => data.filter(x => x.verdict !== '亏损' && (Number(x.lossDays) || 0) > 0), [data]);

  const columns = [
    {
      title: '店铺', dataIndex: 'shop', fixed: 'left', width: 220,
      render: (v) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShopOutlined style={{ color: 'var(--brand)' }} />
          <span style={{ fontWeight: 600, color: '#26201C' }}>{v}</span>
        </div>
      ),
    },
    { title: '广告费', dataIndex: 'fee', width: 100, align: 'right', sorter: (a, b) => a.fee - b.fee, render: v => `¥${yuan(v)}` },
    { title: '单量', dataIndex: 'orders', width: 80, align: 'right', sorter: (a, b) => (Number(a.orders) || 0) - (Number(b.orders) || 0), render: v => `${Number(v) || 0}单` },
    { title: '营收', dataIndex: 'revenue', width: 120, align: 'right', sorter: (a, b) => a.revenue - b.revenue, defaultSortOrder: 'descend', render: v => <span style={{ color: 'var(--brand)', fontWeight: 600 }}>¥{yuan(v)}</span> },
    {
      title: '利润（扣售后+技服费）', dataIndex: 'profit', width: 150, align: 'right', sorter: (a, b) => a.profit - b.profit,
      render: v => <span className={(Number(v) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}>¥{yuan(v)}</span>,
    },
    {
      title: '退款率', dataIndex: 'refundRate', width: 100, align: 'right', sorter: (a, b) => (Number(a.refundRate) || 0) - (Number(b.refundRate) || 0),
      render: (v, r) => (
        <Tooltip title={`退款 ${r.refundOrders} 单，退款金额 ¥${yuan(r.refundAmt)}`}>
          <span style={{ color: (Number(v) || 0) > 5 ? '#CF1322' : '#8C8178' }}>{v}%</span>
        </Tooltip>
      ),
    },
    {
      title: '投产比', dataIndex: 'ratio', width: 90, align: 'right', sorter: (a, b) => (Number(a.ratio) || 0) - (Number(b.ratio) || 0),
      render: v => <span style={{ fontWeight: 700 }}>{v || '—'}</span>,
    },
    {
      title: '扣费后利润', dataIndex: 'netProfit', width: 120, align: 'right', sorter: (a, b) => a.netProfit - b.netProfit,
      render: v => <span className={(Number(v) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}>¥{yuan(v)}</span>,
    },
    {
      title: '日净利连续亏损', dataIndex: 'lossDays', width: 140, align: 'center', sorter: (a, b) => (Number(a.lossDays) || 0) - (Number(b.lossDays) || 0),
      render: (v, r) => v > 0 ? (
        <Tooltip title="点击查看逐日亏损明细（时间/广告费/发货利润/亏损金额）">
          <a onClick={(e) => { e.preventDefault(); openLossDetail(r.shop); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#CF1322', fontWeight: 700 }}>
            <WarningFilled style={{ color: '#CF1322' }} />{v} 天
          </a>
        </Tooltip>
      ) : <span style={{ color: '#B3A79C' }}>—</span>,
    },
    {
      title: '判定', dataIndex: 'verdict', width: 110, align: 'center', fixed: 'right',
      render: v => {
        const m = VERDICT_META[v] || VERDICT_META['未投'];
        return (
          <Tooltip title={m.desc}>
            <Tag color={m.color} style={{ fontSize: 13, padding: '2px 12px', borderRadius: 999 }}>{v}</Tag>
          </Tooltip>
        );
      },
    },
  ];

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#26201C' }}>店铺归因</div>
          <div style={{ color: '#8C8178', fontSize: 12, marginTop: 4 }}>
            按店铺维度判断投放表现：广告费来自「财务分析」手动录入（adSpend），营收/利润按店铺订单聚合（发货口径、已扣售后及技服费 0.7%、排除微信代发单）；默认按月汇集，可切换月份查看；投产比 = 营收 ÷ 广告费
          </div>
        </div>
        <Space wrap>
          <DatePicker
            picker="month"
            value={month}
            onChange={(v) => setMonth(v || null)}
            allowClear
            placeholder="全部月份"
            format="YYYY年MM月"
            style={{ width: 138 }}
            suffixIcon={<CalendarOutlined style={{ color: 'var(--brand)' }} />}
          />
          <Tag color="blue" style={{ fontSize: 13, padding: '2px 12px', borderRadius: 999 }}>判定：可持续 / 可优化 / 亏损 / 未投</Tag>
        </Space>
      </div>

      {/* 汇总统计卡 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': 'var(--brand)' }}>
          <div className="stat-label">总广告费</div>
          <div className="stat-value" style={{ color: 'var(--brand)' }}>¥{yuan(summary.totalFee)}</div>
          <div className="stat-sub">{data.length} 个店铺</div>
        </div></Col>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': '#EF9F27' }}>
          <div className="stat-label">总营收</div>
          <div className="stat-value" style={{ color: '#EF9F27' }}>¥{yuan(summary.totalRev)}</div>
          <div className="stat-sub">共 {summary.totalOrders} 单 · 平均投产比 {summary.avgRatio ? summary.avgRatio.toFixed(2) : '—'}</div>
        </div></Col>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': '#CF1322' }}>
          <div className="stat-label">扣费后利润</div>
          <div className="stat-value" style={{ color: summary.totalNet >= 0 ? '#CF1322' : '#3F8600' }}>¥{yuan(summary.totalNet)}</div>
          <div className="stat-sub">利润 − 广告费</div>
        </div></Col>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': '#8C8178' }}>
          <div className="stat-label">判定分布</div>
          <div className="stat-value" style={{ fontSize: 20, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', paddingTop: 2 }}>
            {Object.entries(summary.dist).map(([k, n]) => n > 0 ? (
              <Tag key={k} color={VERDICT_META[k].color} style={{ margin: 0 }}>{k} {n}</Tag>
            ) : null)}
          </div>
          <div className="stat-sub">可持续 / 可优化 / 亏损 / 未投</div>
        </div></Col>
      </Row>

      {/* 亏损提醒（紧凑单行：月累计为主 / 日净利为参考，详情悬停查看） */}
      {(monthLossShops.length > 0 || dailyLossOnly.length > 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          background: '#FFF9F7', border: '1px solid var(--brand-border)',
          borderLeft: `3px solid ${monthLossShops.length > 0 ? '#CF1322' : '#E8B339'}`,
          borderRadius: 8, padding: '5px 14px', marginBottom: 12, fontSize: 12.5,
        }}>
          <WarningFilled style={{ color: monthLossShops.length > 0 ? '#CF1322' : '#D48806', fontSize: 13 }} />
          {monthLossShops.map(x => (
            <Tooltip key={x.shop} title="月累计口径：本月利润 − 广告费 < 0，建议停投或优化">
              <span style={{ whiteSpace: 'nowrap', cursor: 'default' }}>
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#CF1322', marginRight: 6, verticalAlign: 'middle' }} />
                <b style={{ color: '#26201C' }}>{x.shop}</b>
                <span style={{ color: '#CF1322', fontWeight: 700 }}> 亏损 ¥{yuan(Math.abs(x.netProfit))}</span>
              </span>
            </Tooltip>
          ))}
          {dailyLossOnly.map(x => (
            <Tooltip key={x.shop} title="日净利参考：当日广告费 > 当日发货利润；月累计尚未亏损。点击查看逐日亏损明细">
              <a onClick={(e) => { e.preventDefault(); openLossDetail(x.shop); }} style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#E8B339', marginRight: 6 }} />
                <b style={{ color: '#5F5E5A' }}>{x.shop}</b>
                <span style={{ color: '#AD6800' }}> 连续 {x.lossDays} 天倒挂</span>
                <span style={{ color: '#B3A79C' }}>（参考）</span>
              </a>
            </Tooltip>
          ))}
        </div>
      )}

      {/* 列表 */}
      {data.length === 0 ? (
        <Card><Empty description="还没有店铺数据，订单发货后会自动按店铺聚合" /></Card>
      ) : (
        <Table
          className="link-attr-table"
          columns={columns}
          dataSource={data}
          rowKey="shop"
          size="middle"
          scroll={{ x: 1160 }}
          rowClassName={(r) => {
            if (r.verdict === '亏损') return 'shop-loss-row';            // 月累计亏损：红
            if ((Number(r.lossDays) || 0) > 0) return 'shop-loss-row-soft'; // 日净利连续亏损（参考）：黄
            return '';
          }}
          pagination={{ pageSize: 20, showTotal: t => `共 ${t} 个店铺` }}
        />
      )}

      {/* 亏损明细弹框（日净利口径，点击「日净利连续亏损」提示打开） */}
      <Modal
        open={!!lossModal}
        onCancel={() => setLossModal(null)}
        title={<span style={{ fontSize: 15 }}><WarningFilled style={{ color: '#CF1322', marginRight: 8 }} />{lossModal?.shop} · 亏损明细</span>}
        footer={null}
        width={580}
      >
        {lossLoading || !lossModal?.rows ? (
          <div style={{ padding: '32px 0', textAlign: 'center' }}><Spin /></div>
        ) : (
          <>
            <div style={{ background: '#FFF9F7', border: '1px solid var(--brand-border)', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, color: '#8C8178', marginBottom: 12, lineHeight: 1.8 }}>
              口径：当日广告费 &gt; 当日发货利润 记为一个亏损日（近 90 天，无广告投入日不计）。<br />
              共 <b style={{ color: '#CF1322' }}>{lossModal.count}</b> 个亏损日 · 广告费合计 <b style={{ color: '#26201C' }}>¥{yuan(lossModal.totalAd)}</b> · 发货利润合计 <b style={{ color: '#26201C' }}>¥{yuan(lossModal.totalProfit)}</b> · <b style={{ color: '#CF1322' }}>累计多投 ¥{yuan(lossModal.totalLoss)}</b>
            </div>
            {lossModal.rows.length === 0 ? (
              <Empty description="近 90 天没有亏损日" />
            ) : (
              <Table
                size="small"
                rowKey="date"
                dataSource={lossModal.rows}
                pagination={false}
                scroll={{ y: 320 }}
                columns={[
                  { title: '日期', dataIndex: 'date', width: 110 },
                  { title: '广告费', dataIndex: 'ad', width: 100, align: 'right', render: v => `¥${yuan(v)}` },
                  { title: '当日发货利润', dataIndex: 'profit', width: 130, align: 'right', render: v => <span className={(Number(v) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}>¥{yuan(v)}</span> },
                  { title: '亏损金额', dataIndex: 'loss', width: 110, align: 'right', render: v => <span style={{ color: '#CF1322', fontWeight: 700 }}>¥{yuan(v)}</span> },
                ]}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
